<template>
  <b-modal
      id="product-selection-modal"
      size="xl"
      hide-footer
      :title="modalTitle"
      @hidden="resetModal"
      @show="onShow"
  >
    <div class="product-selection-wrapper">
      <b-input-group class="mb-3">
        <b-form-input
            v-model="searchQuery"
            placeholder="جستجوی محصول..."
            @input="debounceSearch"
        />
        <b-input-group-append is-text>
          <feather-icon icon="SearchIcon" />
        </b-input-group-append>
      </b-input-group>

      <b-overlay :show="loading" rounded="sm">
        <div v-if="products.length" class="product-grid">
          <b-card
              v-for="product in products"
              :key="product.productId"
              class="product-card"
              @click="selectProduct(product)"
          >
            <div class="product-card-body">
              <b-img
                  :src="getProductImage(product)"
                  fluid
                  class="product-thumbnail"
                  alt="تصویر محصول"
              />
              <h6 class="product-title">{{ product.title }}</h6>
              <div class="product-meta">
                <b-badge variant="info">{{ getProductType(product.type) }}</b-badge>
                <span class="product-price" v-if="!product.isFree">
                  {{ makePrettyPrice(product.price) }} تومان
                </span>
                <span class="product-price text-success" v-else>رایگان</span>
              </div>
            </div>
          </b-card>
        </div>

        <b-alert v-else show variant="light" class="text-center">
          محصولی یافت نشد.
        </b-alert>
      </b-overlay>

      <div v-if="totalCount" class="mt-3 d-flex justify-content-center">
        <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            align="center"
            @change="fetchProducts"
        />
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BInputGroup, BFormInput, BInputGroupAppend,
  BOverlay, BCard, BImg, BBadge, BAlert, BPagination
} from 'bootstrap-vue'
import { ProductGetAllRequest } from '@/libs/Api/Product'
import Helper from '@/libs/Helper'
import ToastificationContent from '@core/components/toastification/ToastificationContent'

export default {
  name: 'ProductSelectionModal',
  components: {
    BModal,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BOverlay,
    BCard,
    BImg,
    BBadge,
    BAlert,
    BPagination,
  },
  props: {
    title: {
      type: String,
      default: 'انتخاب محصول'
    },
    productType: {
      type: Number,
      default: null
    },
    perPage: {
      type: Number,
      default: 12
    }
  },
  data() {
    return {
      products: [],
      totalCount: 0,
      loading: false,
      searchQuery: '',
      currentPage: 1,
      searchTimer: null,
      baseUrl: Helper.baseUrl,
    }
  },
  computed: {
    modalTitle() {
      return this.title
    }
  },
  watch: {
    currentPage() {
      this.fetchProducts()
    }
  },
  methods: {
    async fetchProducts() {
      this.loading = true
      const request = new ProductGetAllRequest(this)
      request.setParams({
        pageNumber: this.currentPage,
        count: this.perPage,
        searchByTitle: this.searchQuery.trim(),
        type: 1 || '',
        isFree: '',
        showOnlyInBundle: false,
        categoryId: 0,
      })

      try {
        await request.fetch(
            (content) => {
              this.products = content?.products || []
              this.totalCount = content?.productsCount || 0
            },
            (error) => {
              console.error('Fetch products error:', error)
              this.showErrorToast('دریافت محصولات با خطا مواجه شد.')
              this.products = []
              this.totalCount = 0
            }
        )
      } catch (error) {
        console.error('Unexpected error:', error)
        this.showErrorToast('خطای غیرمنتظره رخ داد.')
        this.products = []
        this.totalCount = 0
      } finally {
        this.loading = false
      }
    },

    debounceSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1
        this.fetchProducts()
      }, 500)
    },

    selectProduct(product) {
      this.$emit('selected', product)
      this.$emit('selected-id', product.productId)
      this.$bvModal.hide('product-selection-modal')
    },

    onShow() {
      this.currentPage = 1
      this.searchQuery = ''
      this.fetchProducts()
    },

    resetModal() {
      this.searchQuery = ''
      this.currentPage = 1
      clearTimeout(this.searchTimer)
    },

    getProductImage(product) {
      if (!product?.imageFile) return '/logo.png'
      const img = String(product.imageFile)
      return img.startsWith('http') ? img : `${this.baseUrl}${img}`
    },

    getProductType(type) {
      const map = {
        1: 'دوره',
        2: 'فیلم',
        3: 'فایل',
        4: 'پادکست',
        6: 'پکیج',
        7: 'باندل',
        8: 'آزمون'
      }
      return map[type] || 'محصول'
    },

    makePrettyPrice(price) {
      return Helper.prettyPrice(Number(price) || 0, 0)
    },

    showErrorToast(message) {
      this.$toast({
        component: ToastificationContent,
        position: 'bottom-center',
        props: {
          title: 'خطا',
          icon: 'AlertCircleIcon',
          variant: 'danger',
          text: message,
        },
      })
    }
  },
  mounted() {
    this.fetchProducts()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
  }
}
</script>

<style lang="scss" scoped>
.product-selection-wrapper {
  min-height: 300px;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
}

.product-card {
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 18px rgba(0,0,0,0.1);
  }
}

.product-card-body {
  text-align: center;
}

.product-thumbnail {
  max-height: 120px;
  object-fit: contain;
  margin-bottom: 0.5rem;
}

.product-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
}

.product-price {
  font-weight: 500;
}
</style>